import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useToast } from '@/hooks/use-toast';

export interface CustomerPaymentMethod {
  id: string;
  organization_id: string;
  stripe_payment_method_id: string;
  payment_method_type: 'card' | 'us_bank_account';
  payment_method_last4: string | null;
  card_brand: string | null;
  bank_name: string | null;
  is_default: boolean;
  is_active: boolean;
  nickname: string | null;
  created_at: string;
  updated_at: string;
}

export function useCustomerPaymentMethods(organizationId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: paymentMethods, isLoading, error, refetch } = useQuery({
    queryKey: ['customer-payment-methods', organizationId],
    queryFn: async () => {
      const { data, error } = await invokeEdgeFunction('list-payment-methods', {
        body: { organizationId },
        callerOperation: 'useCustomerPaymentMethods.list',
      });

      if (error) throw error;
      const result = data as { paymentMethods?: CustomerPaymentMethod[] };
      return result.paymentMethods as CustomerPaymentMethod[];
    },
    enabled: !!organizationId
  });

  const setDefaultMutation = useMutation({
    mutationFn: async (paymentMethodId: string) => {
      const { data, error } = await invokeEdgeFunction('set-default-payment-method', {
        body: { organizationId, paymentMethodId },
        callerOperation: 'useCustomerPaymentMethods.setDefault',
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-payment-methods', organizationId] });
      toast({
        title: 'Default payment method updated',
        description: 'Your default payment method has been changed successfully.'
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error updating default payment method',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (paymentMethodId: string) => {
      const { data, error } = await invokeEdgeFunction('delete-payment-method', {
        body: { organizationId, paymentMethodId },
        callerOperation: 'useCustomerPaymentMethods.delete',
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-payment-methods', organizationId] });
      toast({
        title: 'Payment method deleted',
        description: 'The payment method has been removed successfully.'
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error deleting payment method',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await invokeEdgeFunction('sync-stripe-payment-methods', {
        body: { organizationId },
        callerOperation: 'useCustomerPaymentMethods.sync',
      });

      if (error) throw error;
      return data as { syncedCount?: number };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customer-payment-methods', organizationId] });
      toast({
        title: 'Payment methods synced',
        description: `Successfully synced ${data.syncedCount} payment method(s) from Stripe.`
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error syncing payment methods',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  return {
    paymentMethods: paymentMethods || [],
    isLoading,
    error,
    refetch,
    setDefault: setDefaultMutation.mutate,
    setDefaultPending: setDefaultMutation.isPending,
    deletePaymentMethod: deleteMutation.mutate,
    deletePending: deleteMutation.isPending,
    syncFromStripe: syncMutation.mutate,
    syncPending: syncMutation.isPending
  };
}
